import { sankey, sankeyCenter } from 'd3-sankey'
import {
  LinkExtraProps,
  NodeExtraProps,
  SaFData,
  SaFStyleType
} from '../SaF.types'

type Node = SaFData['nodes'][0]

const emptyLinks: NonNullable<Node['targetLinks']> = []

export const getNodeValues = ({
  targetLinks = emptyLinks,
  sourceLinks = emptyLinks
}: {
  targetLinks?: { value: number }[]
  sourceLinks?: { value: number }[]
}) => {
  const inValue = targetLinks.reduce((sum, link) => sum + link.value, 0)
  const outValue = sourceLinks.reduce((sum, link) => sum + link.value, 0)
  const remainingStock =
    inValue > 0 && outValue > 0 ? Math.max(inValue - outValue, 0) : 0

  return {
    inValue,
    outValue,
    remainingStock
  }
}

export const safNodeWidth = ({ pillWidth, pillsGap }: SaFStyleType) =>
  pillWidth * 2 + pillsGap

export const getNodesByDepth = <TNode extends { depth?: number }>(
  nodes: TNode[]
): TNode[][] =>
  nodes.reduce((result, node) => {
    const { depth = 0 } = node
    const newResult = [...result]
    newResult[depth] = [...(result[depth] || []), node]

    return newResult
  }, [] as TNode[][])

export const getTotalsForNodes = (
  nodes: {
    sourceLinks?: { value: number }[]
    targetLinks?: { value: number }[]
    y0?: number
    y1?: number
  }[]
) => {
  return nodes.reduce<{
    outValue: number
    inValue: number
    remainingStock: number
    y1: number
  }>(
    (valuesSum, node) => {
      const values = getNodeValues(node)

      return {
        outValue: valuesSum.outValue + values.outValue,
        inValue: valuesSum.inValue + values.inValue,
        remainingStock: valuesSum.remainingStock + values.remainingStock,
        y1: valuesSum.y1 + (node.y1 || 0) - (node.y0 || 0)
      }
    },
    { outValue: 0, inValue: 0, remainingStock: 0, y1: 0 }
  )
}

type GenerateSankeyProps = {
  data: SaFData
  columnsLength: number
  columnWidth: number
  safTheme: SaFStyleType
}

export const generateSankey = ({
  data,
  columnsLength,
  columnWidth,
  safTheme
}: GenerateSankeyProps) => {
  const initialSankeyGenerator = sankey<
    NodeExtraProps,
    LinkExtraProps
  >().nodeId((node) => node.id)

  // sankey generator mutates data, we clone to avoid side-effects
  const dataClone: typeof data = JSON.parse(JSON.stringify(data))
  const { nodes: initialNodes } = initialSankeyGenerator(dataClone)

  const nodesByDepth = getNodesByDepth(initialNodes)

  const highestVerticalDepth = nodesByDepth.reduce(
    (result, nodes) => Math.max(result, nodes.length),
    0
  )

  const nodeWidth = safNodeWidth(safTheme)

  const width = columnWidth * (columnsLength - 1) + nodeWidth
  const height = safTheme.chartHeightPerNode * highestVerticalDepth

  const sankeyGenerator = initialSankeyGenerator
    .nodeWidth(nodeWidth)
    .nodePadding(200)
    .size([width, height])
    .nodeAlign(sankeyCenter)

  return { ...sankeyGenerator(dataClone), nodesByDepth, width, height }
}
